import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { ApiError, listAllFeedbackQueryKey, submitFeedbackMutation } from "@/api";

/** Submit feedback; platform admins see it in Admin › Feedback straight away. */
export function useSubmitFeedback() {
  const queryClient = useQueryClient();
  return useMutation({
    ...submitFeedbackMutation(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: listAllFeedbackQueryKey() }),
  });
}

const FEEDBACK_ERRORS = new Set(["FEEDBACK_TITLE_REQUIRED", "FEEDBACK_DESCRIPTION_REQUIRED"] as const);
type FeedbackErrorCode = typeof FEEDBACK_ERRORS extends Set<infer C> ? C : never;

const isFeedbackCode = (code: string | null | undefined): code is FeedbackErrorCode =>
  FEEDBACK_ERRORS.has(code as FeedbackErrorCode);

/** A failed send, translated from the error code or status (backend free text is never shown). */
export function useFeedbackErrorMessage() {
  const { t } = useTranslation("feedback");
  return (error: unknown): string => {
    if (error instanceof ApiError) {
      if (isFeedbackCode(error.code)) return t(`errors.${error.code}`);
      if (error.status === 413) return t("errors.tooLarge");
      if (error.status === 422) return t("errors.invalid");
    }
    return t("sendFailed");
  };
}
